// ── the night rooms' figures ────────────────────────────────────────────────
// Pictures for /company/about and /company/team, drawn in the house style the
// community letter and the guides set (about-figures.css): cream sheets set
// down on a deep ground at a slight tilt, netti's light behind them, the
// organism's hue the only colour. Each one says something a paragraph cannot:
// what sits underneath an outcome, what "one memory, many tools" looks like,
// how one conversation becomes a history, what a branch is for, what a tool is
// actually allowed to read.
//
// Every product word here (a tool name, `innernet_context`, the five classes,
// the `## history` line) is the real one; the examples are illustrations and
// say so by being ordinary (pricing, a launch), never a real person's data.
//
// No hooks: safe inside the client rail and on the server alike.

import React from "react";
import AppIcon from "../brand/AppIcon";
import BrandIcon from "../BrandIcon";
import OrganismMark from "../brand/OrganismMark";
import "./about-figures.css";

type Style = React.CSSProperties & Record<`--${string}`, string | number>;

export function Fig({ caption, children, label }: { caption?: string; label: string; children: React.ReactNode }) {
  return (
    <figure className="abv" aria-label={label}>
      <div className="abv__stage">{children}</div>
      {caption && <figcaption className="abv__cap">{caption}</figcaption>}
    </figure>
  );
}

function Glow({ x = "50%", y = "50%", size = "60%" }: { x?: string; y?: string; size?: string }) {
  return <span className="abv-glow" style={{ "--gx": x, "--gy": y, "--gs": size } as Style} aria-hidden />;
}

function Card({
  top,
  title,
  dek,
  tags,
  foot,
  live,
  r = 0,
  drift,
  style,
  children,
}: {
  top?: React.ReactNode;
  title?: React.ReactNode;
  dek?: React.ReactNode;
  tags?: (string | [string, true])[];
  foot?: React.ReactNode;
  live?: boolean;
  r?: number;
  drift?: [number, number];
  style?: Style;
  children?: React.ReactNode;
}) {
  const s: Style = { "--r": `${r}deg`, ...(drift ? { "--dd": `${drift[0]}s`, "--dl": `${drift[1]}s` } : {}), ...style };
  return (
    <div className="abv-card" style={s} data-drift={drift ? "" : undefined}>
      {(top || live) && (
        <div className="abv-card__top">
          {top}
          {live && <span className="abv-live" aria-hidden />}
        </div>
      )}
      {title && <p className="abv-card__title">{title}</p>}
      {dek && <p className="abv-card__dek">{dek}</p>}
      {children}
      {tags && (
        <div className="abv-tags">
          {tags.map((t) =>
            Array.isArray(t) ? (
              <span key={t[0]} className="abv-tag" data-lit="">{t[0]}</span>
            ) : (
              <span key={t} className="abv-tag">{t}</span>
            ),
          )}
        </div>
      )}
      {foot && <div className="abv-card__foot">{foot}</div>}
    </div>
  );
}

/** netti's mark in its SOLID tone. Never a drawn gradient cell: a stand-in
 *  for netti reads as a placeholder, the mark reads as us. */
export function Netti({ size = 44 }: { size?: number }) {
  return (
    <span className="abv-mark" aria-hidden>
      <OrganismMark size={size} tone="solid" />
    </span>
  );
}

// ── A · underneath ─────────────────────────────────────────────────────────
// The outcome is the one sharp sheet. Everything it came from lies under it,
// out of focus, which is how it usually goes.

const UNDER: [string, string, number, number, number][] = [
  // eyebrow, words, tilt, blur px, opacity
  ["never written down", "what if the whole thing started from the other end?", -6, 3.2, 0.46],
  ["abandoned · march", "a version for teams first. dropped after two weeks.", 4, 2.2, 0.56],
  ["kept returning to", "why does every tool forget me by morning?", -3, 2.6, 0.5],
  ["the reason", "we chose the slower build because it would still be true in a year.", 5, 1.8, 0.6],
  ["take 7 of 11", "slower. no, the bridge is wrong.", -4, 2.4, 0.52],
  ["the moment it clicked", "it isn't a notebook. it's a memory with a keeper.", 3, 1.6, 0.62],
];

export function FigUnderneath() {
  return (
    <Fig
      label="the outcome as one sharp card, resting on a pile of the ideas, reasons and versions it came from"
      caption="we keep the top card. the real story is the pile underneath it."
    >
      <Glow size="70%" />
      <div className="abv-pile" aria-hidden>
        {UNDER.map(([top, words, r, b, o], i) => (
          <Card key={top} top={top} dek={words} r={r} drift={[9 + (i % 3) * 1.4, -i * 1.3]} style={{ "--b": `${b}px`, "--o": o }} />
        ))}
      </div>
      <div className="abv-pile__front">
        <Card
          top={<>shipped · v1.0</>}
          live
          title="the outcome"
          dek="the song. the book. the product. the decision. the part everyone gets to see."
          tags={["what survives"]}
          foot="the surface"
          r={-1.5}
        />
      </div>
    </Fig>
  );
}

// ── B · many above, one beneath ────────────────────────────────────────────

export type Above = { key: string; label: string; mark: React.ReactNode };

export function Threads({ n, upEvery = 2 }: { n: number; upEvery?: number }) {
  // columns are equal, so each item's centre is (i + .5) / n across
  const xs = Array.from({ length: n }, (_, i) => ((i + 0.5) / n) * 100);
  return (
    <svg className="abv-threads" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
      {xs.map((x, i) => {
        const d = `M${x} 0 C ${x} 55, 50 45, 50 100`;
        return (
          <g key={i}>
            <path className="t-base" d={d} />
            <path
              className="t-pulse"
              d={d}
              pathLength={1}
              data-up={i % upEvery === 1 ? "" : undefined}
              style={{ "--fd": `${3.2 + (i % 3) * 0.6}s`, "--fl": `${-i * 0.9}s` } as Style}
            />
          </g>
        );
      })}
    </svg>
  );
}

export function Converge({ above, children }: { above: Above[]; children: React.ReactNode }) {
  return (
    <>
      <div className="abv-tools" style={{ "--cols": above.length } as Style}>
        {above.map((a, i) => (
          <div key={a.key} className="abv-tool" data-drift="" style={{ "--dd": `${7 + (i % 3)}s`, "--dl": `${-i * 1.1}s` } as Style}>
            {a.mark}
            <span>{a.label}</span>
          </div>
        ))}
      </div>
      <Threads n={above.length} />
      <div className="abv-base">{children}</div>
    </>
  );
}

const TOOLS: Above[] = [
  { key: "claude", label: "claude", mark: <AppIcon name="claude" size={44} /> },
  { key: "cursor", label: "cursor", mark: <AppIcon name="cursor" size={44} /> },
  { key: "codex", label: "codex", mark: <AppIcon name="codex" size={44} /> },
  { key: "chatgpt", label: "chatgpt", mark: <AppIcon name="openai" size={44} label="chatgpt" /> },
  { key: "next", label: "whatever comes next", mark: <span className="abv-next" aria-hidden>+</span> },
];

export function FigOneMemory() {
  return (
    <Fig
      label="claude, cursor, codex, chatgpt and whatever comes next all read from and write to one memory beneath them"
      caption="every tool reads from it and writes to it. none of them owns it."
    >
      <Glow y="78%" size="64%" />
      <Converge above={TOOLS}>
        <Card r={-0.6}>
          <div className="abv-base__head">
            <Netti size={46} />
            <div>
              <p className="abv-card__title">one memory, yours</p>
              <p>kept by netti · reached over mcp and an open api</p>
            </div>
          </div>
          <div className="abv-tags" style={{ marginTop: "0.85rem" }}>
            {["ideas", "people", "decisions", "projects", "knowledge"].map((t) => (
              <span key={t} className="abv-tag">{t}</span>
            ))}
          </div>
        </Card>
      </Converge>
    </Fig>
  );
}

// ── C · becoming ───────────────────────────────────────────────────────────

export function FigBecoming() {
  return (
    <Fig
      label="one conversation becomes a decision, the decision becomes part of a project, and the project keeps a history"
      caption="one sentence in a chat, four lives after it. nobody filed any of it."
    >
      <Glow x="80%" y="40%" size="46%" />
      <div className="abv-chain">
        <Card top={<><span className="abv-step">1</span> a conversation</>} r={-3} drift={[9, 0]}>
          <span className="abv-say">seats punish the teams who use it most. let&rsquo;s go usage-based.</span>
          <span className="abv-call"><BrandIcon name="innernet" size={12} />innernet_capture</span>
        </Card>
        <Card top={<><span className="abv-step">2</span> a decision</>} title="pricing is usage-based" dek="from a chat in claude, on the 12th." r={2} drift={[10, -2]} />
        <Card top={<><span className="abv-step">3</span> part of a project</>} r={-1.5} drift={[8.4, -4]}>
          <p className="abv-card__title">launch</p>
          <div className="abv-rows">
            <div className="abv-row"><span>pricing</span><span>updated</span></div>
            <div className="abv-row"><span>onboarding</span><span>2d</span></div>
            <div className="abv-row"><span>the landing page</span><span>6d</span></div>
          </div>
        </Card>
        <Card top={<><span className="abv-step">4</span> a history</>} r={2.5} drift={[9.6, -1]} live>
          <p className="abv-card__title">## history</p>
          <div className="abv-hist">
            <span>12 sep: was <del>per seat</del> → now <mark>usage-based</mark></span>
            <span>3 jun: was <del>free</del> → now per seat</span>
          </div>
        </Card>
      </div>
    </Fig>
  );
}

// ── D · a position has a past ──────────────────────────────────────────────

export function FigPast() {
  return (
    <Fig
      label="a line of versions from march to september, with a parked branch off it that is still there"
      caption="the answer moved three times. every version is still there, and so is the one you didn't take."
    >
      <div className="abv-line">
        <div className="abv-commit">
          <Card top="march" title="free, for now" dek="get it into hands first." r={-1.5} />
          <span className="abv-commit__node" aria-hidden />
        </div>
        <div className="abv-commit">
          <Card top="june" title="per seat" dek="the obvious model. it held for a summer." r={1.5} />
          <span className="abv-commit__node" aria-hidden />
        </div>
        <div className="abv-commit" data-branch="">
          <Card top="parked" title="a lifetime deal" dek="not now. maybe later." r={-2} />
          <span className="abv-commit__node" aria-hidden />
        </div>
        <div className="abv-commit" data-now="">
          <Card top="september · now" title="usage-based" dek="because of what june taught us." live r={1} />
          <span className="abv-commit__node" aria-hidden />
        </div>
      </div>
    </Fig>
  );
}

// ── E · the guest changes, the memory doesn't ──────────────────────────────

export function FigGuests() {
  return (
    <Fig
      label="on monday a decision is made in claude; on tuesday cursor already knows it"
      caption="a different guest, the same memory. nothing explained twice."
    >
      <Glow x="50%" y="60%" size="70%" />
      <div className="abv-guests">
        <Card r={-1.5} drift={[10, 0]}>
          <div className="abv-window__bar"><AppIcon name="claude" size={20} /><b>claude</b> · monday</div>
          <span className="abv-say">let&rsquo;s go usage-based. seats punish the teams who use it most.</span>
          <span className="abv-call"><BrandIcon name="innernet" size={12} />innernet_capture</span>
          <span className="abv-reply">kept. launch · pricing now says usage-based.</span>
        </Card>
        <Card r={1.2} drift={[9, -3]}>
          <div className="abv-window__bar"><AppIcon name="cursor" size={20} /><b>cursor</b> · tuesday</div>
          <span className="abv-say">wire up the billing page.</span>
          <span className="abv-call"><BrandIcon name="innernet" size={12} />innernet_context</span>
          <span className="abv-reply">usage-based, as you decided yesterday. starting with the meter.</span>
        </Card>
      </div>
    </Fig>
  );
}

// ── F · how far a fact may travel ──────────────────────────────────────────

const CLASSES: { name: string; fact: string; who: string; sent: boolean }[] = [
  { name: "private", fact: "a note only you will read", who: "never leaves you", sent: false },
  { name: "self", fact: "you write best before noon", who: "your own page", sent: false },
  { name: "trusted", fact: "you prefer typescript", who: "your signed-in tools", sent: true },
  { name: "work", fact: "the launch is on the 14th", who: "work projects", sent: true },
  { name: "public", fact: "your published skill", who: "anyone", sent: true },
];

export function FigBoundaries() {
  return (
    <Fig
      label="five classes of fact, from private to public; a tool reading for a work project gets trusted, work and public, and the rest is held back"
      caption="checked at the moment of reading, one fact at a time. the rest stays where it is."
    >
      <span className="abv-reader">
        <AppIcon name="cursor" size={22} />
        <span><b>cursor</b> is reading, for a work project</span>
      </span>
      <div className="abv-classes">
        {CLASSES.map((c, i) => (
          <div key={c.name} className="abv-class" data-held={c.sent ? undefined : ""}>
            <Card r={i % 2 ? 1 : -1} title={c.name} foot={<span className="abv-verdict" data-sent={c.sent ? "" : undefined}><span>{c.sent ? "✓ sent" : "held back"}</span><span style={{ opacity: 0.7 }}>{c.who}</span></span>}>
              <span className="abv-say" style={{ marginTop: "0.5rem", fontSize: "0.76rem" }}>{c.fact}</span>
            </Card>
          </div>
        ))}
      </div>
    </Fig>
  );
}
